
import { messageBundle, storeNode, tsx } from "@arcgis/core/widgets/support/widget";
import {
  property,
  subclass
} from "@arcgis/core/core/accessorSupport/decorators";
import Widget from '@arcgis/core/widgets/Widget';
import SearchBoxViewModel from "./SearchBox/SearchBoxViewModel";
import "./SearchBox/styles/SearchBox.scss";

import { VNode } from "../../interfaces/interfaces";
import { whenDefinedOnce, whenTrueOnce } from "@arcgis/core/core/watchUtils";
import NavBarViewModel from '../ui/NavBar/NavBarViewModel';
import { applyShadowStyle } from '../utils/utils';
import { INPUT_SEARCH_STYLE } from '../utils/shadowStyles';
import { logCapabilitySearch } from '../utils/telemetryUtils';

const CSS = {
  base: "esri-instant-gallery__search-settings",
  container: "esri-instant-gallery__search-settings-container",
  suggestedResultsList: "esri-instant-gallery__search-settings-results-list",
  hideSuggestedResultsList:
    "esri-instant-gallery__search-settings-results-list--hide",
  suggestedResultsListItem:
    "esri-instant-gallery__search-settings-results-list-item",
  resultListItemBold:
    "esri-instant-gallery__search-settings-results-list-item--bold",
  resultsItemWarning: "esri-search-settings__result-item-warning",
  noResultsListItem: "esri-instant-gallery__search-settings-no-results-item",
  highlightedResultItem:
    "esri-instant-gallery__search-settings-results-list-item--highlighted",
  headerTopContainer: "esri-instant-gallery__search-settings-header-top-container",
  headerContainer: "esri-instant-gallery__search-settings-header-container",
  closeButton: "esri-instant-gallery__search-settings-close-button"
};

@subclass("SearchBox")
export default class SearchBox extends Widget {
  constructor(params?: any) {
    super(params);
  }

  private _hasEventListener = false;
  private _suggestionsListNode: HTMLLIElement = null;
  private _suggestedResultIndex: number = null;
  /** For normal search box with suggestion behavior */
  @property()
  _hideSuggestions = false;
  /** 
   * Master switch that turns off suggestions on the search box (when value is true) 
   * As of 5/23/2021, this being true implies that we are on the "My Apps" tab
  */
  @property()
  _hideSuggestions_masterOverride: boolean = false;
  @property()
  _placeholderToSet: string;

  @property()
  searchInput: HTMLCalciteInputElement = null;

  @property()
  @messageBundle("dist/assets/t9n/App/App")
  appMessages;

  @property()
  viewModel: SearchBoxViewModel = new SearchBoxViewModel();

  @property()
  navbarVM: NavBarViewModel;

  postInitialize() {
    whenDefinedOnce(this, "appMessages", () => {
      this.viewModel.appMessages = this.appMessages;
    });
  }

  setSearchBoxString(strVal: string, placeHolderText: string, showSuggestions?: boolean) {
    if (showSuggestions != null) {
      this._hideSuggestions_masterOverride = !showSuggestions;
    }
    if (this.searchInput != null) {
      this.searchInput.value = strVal;
      this.searchInput.placeholder = placeHolderText;
      this.scheduleRender();
    } else {
      // We are defaulting to a different tab on app load, but the search doesn't exist yet, so save the intended placeholder text and set it later
      this._placeholderToSet = placeHolderText;
    }
  }

  render(): VNode {
    const suggestedResultsList = this._renderSuggestedResultsList();
    return (
      <div 
        key="search-settings"
        class={CSS.base}
      >
        <div 
          class={CSS.container} 
          afterCreate={this._setupFocusOutHandler.bind(this)} 
          tabindex={this._isSearchBoxDisabled() ? "0" : "-1"}
          onclick={this._handleSelectSearchBox.bind(this)}
          onkeyup={(e: KeyboardEvent)=>{
            if(e.code === "Enter" || e.code === "Space"){
              this._handleSelectSearchBox();
            }
          }}
        >
          <calcite-input
            scale="l"
            bind={this}
            afterCreate={(input) => {
              this.searchInput = input;
              applyShadowStyle(INPUT_SEARCH_STYLE, input);
            }}
            afterUpdate={this._addCalciteInputEventListener}
            data-node-ref="searchInput"
            type="search"
            placeholder={this._placeholderToSet ?? this.appMessages.searchBox.search_appTypes}
            icon="search"
            disabled={this._isSearchBoxDisabled()}
            autocomplete="off"
            onkeyup={this._handleInputKeyUp}
            onkeydown={this._handleResultKeyDown}
          />
          {suggestedResultsList}
        </div>
      </div>
    );
  }

  private _renderSuggestedResultsList(): VNode {
    const resultsListItems = this._renderSuggestedResultsListItems();
    const resultsLength = this.viewModel.searchResults.length;
    const hideStyles = {
      [CSS.hideSuggestedResultsList]: this._hideSuggestions_masterOverride || this._hideSuggestions
    };
    return resultsLength > 0 && this.searchInput?.value?.length > 0 ? (
      <ul
        bind={this}
        afterCreate={storeNode}
        data-node-ref="_suggestionsListNode"
        class={this.classes(CSS.suggestedResultsList, hideStyles)}
        role="menu"
      >
        {resultsListItems}
      </ul>
    ) : resultsLength === 0 && this.searchInput?.value?.length > 0 ? (
      <ul class={this.classes(CSS.suggestedResultsList, hideStyles)} role="menu">
        {this._renderNoResultsListItem()}
      </ul>
    ) : null;
  }

  private _renderNoResultsListItem(): VNode {
    return (
      <li key="no-results-item" class={CSS.noResultsListItem}>
        {
          this.appMessages.searchResults.noResults
        }
      </li>
    );
  }

  private _renderSuggestedResultsListItems(): VNode[] {
    const searchResultsArr = this.viewModel.searchResults.toArray();
    return searchResultsArr.map((searchResult, searchResultIndex) => {
      const suggestedResult = this._renderSuggestedResultListItem(
        searchResult,
        searchResultIndex
      );
      return suggestedResult;
    });
  }

  private _renderSuggestedResultListItem(
    searchResult: string,
    searchResultIndex: number
  ): VNode {
    const splitResultText = searchResult?.toLocaleLowerCase()?.split(this.viewModel?.currentSearchValue?.toLocaleLowerCase());
    return (
      <li
        key={`${"results"}-${searchResultIndex}`}
        bind={this}
        class={this.classes(CSS.suggestedResultsListItem)}
        onclick={this._handleResultClick}
        onkeyup={this._handleInputKeyUp}
        onkeydown={this._handleResultKeyDown}
        role="menuitem"
        data-result-index={`${searchResultIndex}`}
        data-term-value={searchResult}
      >
        <div key={`${"resultsDiv"}-${searchResultIndex}`}>
          {this._renderResultTextWithBolding(splitResultText)}
        </div>
      </li>
    );
  }

  private _renderResultTextWithBolding(splitResultText: string[]): VNode[] {
    return splitResultText.map((searchWordPart: string, index: number) => {
      return splitResultText.length - 1 !== index
        ? [
          <span key={`${"resultsSpan"}-${index}_1`}>{searchWordPart}</span>,
          <span key={`${"resultsSpan"}-${index}_2`} class={CSS.resultListItemBold}>{this?.viewModel?.currentSearchValue?.toLocaleLowerCase()}</span>
        ]
        : <span key={`${"resultsSpan"}-${index}`}>{searchWordPart}</span>
    });
  }

  private _addCalciteInputEventListener(node: HTMLCalciteInputElement): void {
    if (this._hasEventListener) {
      return;
    }
    this._hasEventListener = true;
    node.addEventListener("calciteInputInput", (e: CustomEvent) => {
      if (this._hideSuggestions_masterOverride) { // "MY APPS" SEARCH BOX BEHAVIOR
        const { value } = e.detail;
        this.navbarVM.appSearch(value);
      } else { // "BROWSE ALL" and "FEATURED" SEARCH BOX BEHAVIOR
        this._calciteInputInput(e);
      }
    });
    node.addEventListener("calciteInputFocus", (e: CustomEvent) => {
      this._calciteInputFocus();
    });
  }

  private _calciteInputInput(e: CustomEvent): void {
    this._suggestedResultIndex = null;
    this.viewModel.handleSearch(e);
    this.scheduleRender();
  }

  private _calciteInputFocus(): void {
    this._hideSuggestions = false;
    this.scheduleRender();
  }

  private _searchBoxFocusOut(): void {
    this._hideSuggestions = true;
    this.scheduleRender();
  }

  private _previousFocusedListItem: HTMLLIElement;
  private _removePreviousHighlight() {
    if (this._previousFocusedListItem != null) {
      this._previousFocusedListItem.classList.remove(CSS.highlightedResultItem);
    }
  }

  private _handleInputKeyUp(e: KeyboardEvent): void {
    if (e.code === "Escape") {
      this.searchInput.setFocus();
    }
    if (!this._suggestionsListNode) {
      return;
    }

    if (e.code === "ArrowDown" || e.code === "ArrowUp") {
      const listItemNodes = this._suggestionsListNode.getElementsByTagName("li");
      const firstListItemNode = listItemNodes[0];
      const lastItemIndex = listItemNodes.length - 1;
      const lastListItemNode = listItemNodes[lastItemIndex];
      if (listItemNodes.length > 0) {
        if (this._suggestedResultIndex === null) {
          let currentListItem: HTMLLIElement;
          if (e.code === "ArrowDown") {
            this._suggestedResultIndex = 0;
            currentListItem = firstListItemNode;
          } else if (e.code === "ArrowUp") {
            this._suggestedResultIndex = lastItemIndex;
            currentListItem = lastListItemNode;
          }
          currentListItem.classList.add(CSS.highlightedResultItem);
          this._removePreviousHighlight();
          this._previousFocusedListItem = currentListItem;

        } else if ((e.code === "ArrowDown" && this._suggestedResultIndex === listItemNodes.length - 1) || (e.code === "ArrowUp" && this._suggestedResultIndex === 0)) {
          this._suggestedResultIndex = null;
          this.searchInput.setFocus();
          this._removePreviousHighlight();
          this._previousFocusedListItem = null;

        } else {
          if (e.code === "ArrowDown") {
            this._suggestedResultIndex += 1;
          } else if (e.code === "ArrowUp") {
            this._suggestedResultIndex -= 1;
          }
          const listItemNode = listItemNodes[this._suggestedResultIndex];
          listItemNode.classList.add(CSS.highlightedResultItem);
          this._removePreviousHighlight();
          this._previousFocusedListItem = listItemNode;
        }
      }
    }
  }

  private _handleResultClick(e: Event): void {
    const node = e.currentTarget as HTMLLIElement;
    this._handleResultSelectionNode(node);
  }

  private _handleResultKeyDown(e: KeyboardEvent): void {
    if (e.code === "Tab") {
      return;
    }
    if (this._hideSuggestions) {
      this._hideSuggestions = false;
    }
    if (e.type === "keydown" && e.code !== "Space" && e.code !== "Enter") {
      return;
    }
    const selectedNode = this._suggestionsListNode?.querySelector(
      `[data-result-index='${this._suggestedResultIndex}']`
    ) as HTMLLIElement;
    if (!selectedNode) {
      return;
    }
    this._handleResultSelectionNode(selectedNode);
  }

  private _handleResultSelectionNode(node: HTMLLIElement): void {
    const selectionValue = node.getAttribute("data-term-value");

    this._suggestedResultIndex = null;
    this._hideSuggestions = true;
    this.searchInput.value = null;
    
    if(this.navbarVM.isFeaturedTab() && this.navbarVM.suggestionsButtonSignal){
      this.navbarVM.toggleSuggBtnSignal();
    }

    this.navbarVM.setNavbarRoute("browseAll");
    this.navbarVM.addCapabililty(selectionValue);

    logCapabilitySearch(
      this.navbarVM.telemetry,
      selectionValue,
      this.navbarVM.navigationRoute,
      this.navbarVM.filterChips.chips.length
    );
  }

  private _setupFocusOutHandler(encompassingDiv: HTMLDivElement) {
    encompassingDiv.addEventListener("focusout", (e: FocusEvent) => {
      const elemFocusWentTo: HTMLElement = e.relatedTarget as HTMLElement;
      let focusedElemIsDescendant = false; // is elemFocusWentTo a descendant of encompassingDiv?
      let descendantChainMember = elemFocusWentTo;

      // loop up the parentElement until DOM Document is reached (document.parentElement === null)
      while (descendantChainMember != null) {
        if (descendantChainMember === encompassingDiv) {
          focusedElemIsDescendant = true;
        }
        descendantChainMember = descendantChainMember.parentElement;
      }
      if (!focusedElemIsDescendant) {
        const isElemFocusWentToNull = elemFocusWentTo === null; // implies a click happened to cause focusout
        setTimeout(() => {
          this._searchBoxFocusOut();
        }, isElemFocusWentToNull ? 500 : 0)
      }
    })
  }

  private _isSearchBoxDisabled(): boolean {
    return this.navbarVM.suggestionsButtonSignal && this.navbarVM.navigationRoute === "browseAll";
  }

  private _handleSelectSearchBox():void{
    if (this.navbarVM.suggestionsButtonSignal 
      && (this.navbarVM.navigationRoute === "browseAll")) {
      this.navbarVM.toggleSuggBtnSignal();
      setTimeout(() => { this.searchInput.setFocus(); }, 100)
    }
  }
}

